// import { API_BASE_URL } from '../config';
// import { apiClient } from './apiClient'; // Eliminamos esta importación

// Elimina la lógica de authToken, setToken y removeToken, ya que la sesión es por cookie.
// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000'; // Ya no es necesario si no hay login directo
const buildUrl = (path) => `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${path}`;

const handleResponse = async (response, options = {}) => {
  if (response.status === 401) {
    throw new Error('No autenticado o sesión inválida');
  }
  if (response.status === 403 && options.accessDeniedKey) {
    const data = await response.json().catch(() => ({}));
    const err = new Error(data.detail || 'No tiene permisos para acceder a la aplicación.');
    err.code = 'ACCESS_DENIED';
    throw err;
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText || 'Error en la petición' }));
    throw new Error(error.detail || 'Error en la petición');
  }
  if (response.status === 204) {
    return null;
  }
  return response.json();
};

const api = {
  // getHeaders ya no necesita enviar el token, la cookie se envía automáticamente
  getHeaders: () => ({
    'Content-Type': 'application/json',
  }),

  // login y handleAuthCallback se eliminan, el backend los gestiona.

  getCurrentUser: async () => {
    const response = await fetch(buildUrl('/api/auth/me'), {
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response, { accessDeniedKey: true });
  },

  // Usuarios
  getUsers: async () => {
    const response = await fetch(buildUrl('/api/users'), { 
      headers: api.getHeaders(), 
      credentials: 'include',
    });
    return handleResponse(response);
  },

  createUser: async (userData) => {
    const response = await fetch(buildUrl('/api/users'), {
      method: 'POST',
      headers: api.getHeaders(), // Usar getHeaders para consistencia y auth
      body: JSON.stringify(userData),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  updateUser: async (userId, userData) => {
    const response = await fetch(buildUrl(`/api/users/${userId}`), {
      method: 'PUT',
      headers: api.getHeaders(),
      body: JSON.stringify(userData),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  deleteUser: async (userId) => {
    const response = await fetch(buildUrl(`/api/users/${userId}`), {
      method: 'DELETE',
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  syncUsersFromSlack: async () => {
    const response = await fetch(buildUrl('/api/users/sync-slack'), {
      method: 'POST',
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  forceDailyStandup: async (userId) => {
    const response = await fetch(buildUrl(`/api/users/${userId}/force-daily`), {
      method: 'POST',
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // Respuestas diarias
  async getResponses(startDate, endDate, userId = 'all', team = 'all') {
    const url = new URL(buildUrl('/api/responses'));
    url.searchParams.append('start_date', startDate);
    url.searchParams.append('end_date', endDate);
    url.searchParams.append('user_id', userId);
    url.searchParams.append('team', team);
    
    const response = await fetch(url.toString(), { 
      headers: api.getHeaders(), 
      credentials: 'include',
    });
    return handleResponse(response);
  },

  saveResponse: async (responseData) => {
    const response = await fetch(buildUrl('/api/responses'), {
      method: 'POST',
      headers: api.getHeaders(),
      body: JSON.stringify(responseData),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // Participación en dailies
  async getDailyParticipation(date, team = 'all') {
    const url = new URL(buildUrl('/api/daily-participation'));
    url.searchParams.append('date', date);
    url.searchParams.append('team', team);
    
    const response = await fetch(url.toString(), { 
      headers: api.getHeaders(), 
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // Configuración de Slack
  getSlackConfig: async () => {
    const response = await fetch(buildUrl('/api/slack/config'), { 
      headers: api.getHeaders(), 
      credentials: 'include',
    });
    return handleResponse(response);
  },

  updateSlackConfig: async (config) => {
    const response = await fetch(buildUrl('/api/slack/config'), {
      method: 'PUT',
      headers: api.getHeaders(),
      body: JSON.stringify(config),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // Usuarios de Slack
  getSlackUsers: async () => {
    const response = await fetch(buildUrl('/api/slack/users'), { 
      headers: api.getHeaders(), 
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // Catálogo de proyectos de JIRA
  getJiraProjects: async () => {
    const response = await fetch(buildUrl('/api/v1/jira-projects/'), {
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  createJiraProject: async (data) => {
    const response = await fetch(buildUrl('/api/v1/jira-projects/'), {
      method: 'POST',
      headers: api.getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  updateJiraProject: async (projectId, data) => {
    const response = await fetch(buildUrl(`/api/v1/jira-projects/${projectId}`), {
      method: 'PUT',
      headers: api.getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  deleteJiraProject: async (projectId) => {
    const response = await fetch(buildUrl(`/api/v1/jira-projects/${projectId}`), {
      method: 'DELETE',
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // Miembros de un proyecto de JIRA (por id o por clave) con sus roles
  getJiraProjectMembers: async (idOrKey, { role, includeInactive = false } = {}) => {
    const params = new URLSearchParams();
    if (role) params.append('role', role);
    if (includeInactive) params.append('include_inactive', 'true');
    const query = params.toString();
    const response = await fetch(buildUrl(`/api/v1/jira-projects/${idOrKey}/members${query ? `?${query}` : ''}`), {
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // Quién del proyecto de JIRA no está hoy (o el día indicado), con sus roles en el proyecto
  getJiraProjectVacationsToday: async (idOrKey, { date, role, includeTeletrabajo = false, includeHolidays = true } = {}) => {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (role) params.append('role', role);
    if (includeTeletrabajo) params.append('include_teletrabajo', 'true');
    if (!includeHolidays) params.append('include_holidays', 'false');
    const query = params.toString();
    const response = await fetch(buildUrl(`/api/v1/jira-projects/${idOrKey}/vacations/today${query ? `?${query}` : ''}`), {
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // Catálogo de roles
  getRoles: async () => {
    const response = await fetch(buildUrl('/api/v1/roles/'), {
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  createRole: async (data) => {
    const response = await fetch(buildUrl('/api/v1/roles/'), {
      method: 'POST',
      headers: api.getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  updateRole: async (roleId, data) => {
    const response = await fetch(buildUrl(`/api/v1/roles/${roleId}`), {
      method: 'PUT',
      headers: api.getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  deleteRole: async (roleId) => {
    const response = await fetch(buildUrl(`/api/v1/roles/${roleId}`), {
      method: 'DELETE',
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // Calendario de Vacaciones
  getVacationCalendarEvents: async () => {
    const response = await fetch(buildUrl('/api/calendar/vacations'), { 
      headers: api.getHeaders(), 
      credentials: 'include',
    });
    return handleResponse(response);
  },

  forceCalendarSync: async () => {
    const response = await fetch(buildUrl('/api/calendar/sync'), {
      method: 'POST',
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  getAvailableDates: async () => {
    const response = await fetch(buildUrl('/api/responses/dates'), { 
      headers: api.getHeaders(), 
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // Equipos
  getTeams: async () => {
    const response = await fetch(buildUrl('/api/v1/teams'), { 
      headers: api.getHeaders(), 
      credentials: 'include',
    });
    return handleResponse(response);
  },

  createTeam: async (teamData) => {
    const response = await fetch(buildUrl('/api/v1/teams'), {
      method: 'POST',
      headers: api.getHeaders(),
      body: JSON.stringify(teamData),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  updateTeam: async (teamId, teamData) => {
    const response = await fetch(buildUrl(`/api/v1/teams/${teamId}`), {
      method: 'PUT',
      headers: api.getHeaders(),
      body: JSON.stringify(teamData),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  deleteTeam: async (teamId) => {
    const response = await fetch(buildUrl(`/api/v1/teams/${teamId}`), {
      method: 'DELETE',
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // Quién no está hoy (o el día indicado): ausencias, festivos y, si se pide, teletrabajo
  getVacationsToday: async ({ date, team, includeTeletrabajo = false, includeHolidays = true } = {}) => {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (team && team !== 'all') params.append('team', team);
    if (includeTeletrabajo) params.append('include_teletrabajo', 'true');
    if (!includeHolidays) params.append('include_holidays', 'false');
    const query = params.toString();
    const response = await fetch(buildUrl(`/api/calendar/vacations/today${query ? `?${query}` : ''}`), {
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  getVacationEvents: async (month, year) => {
    const params = new URLSearchParams();
    if (month) params.append('month', month);
    if (year) params.append('year', year);
    const response = await fetch(buildUrl(`/api/calendar/vacations?${params.toString()}`), {
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  createVacation: async (vacationData) => {
    const response = await fetch(buildUrl('/api/calendar/vacations'), {
      method: 'POST',
      headers: api.getHeaders(),
      body: JSON.stringify(vacationData),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  updateVacation: async (vacationId, vacationData) => {
    const response = await fetch(buildUrl(`/api/calendar/vacations/${vacationId}`), {
      method: 'PUT',
      headers: api.getHeaders(),
      body: JSON.stringify(vacationData),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  deleteVacation: async (vacationId) => {
    const response = await fetch(buildUrl(`/api/calendar/vacations/${vacationId}`), {
      method: 'DELETE',
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // Calendarios de festividades
  getHolidayCalendars: async () => {
    const response = await fetch(buildUrl('/api/holiday-calendars/'), {
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  createHolidayCalendar: async (data) => {
    const response = await fetch(buildUrl('/api/holiday-calendars/'), {
      method: 'POST',
      headers: api.getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  updateHolidayCalendar: async (calendarId, data) => {
    const response = await fetch(buildUrl(`/api/holiday-calendars/${calendarId}`), {
      method: 'PUT',
      headers: api.getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  deleteHolidayCalendar: async (calendarId) => {
    const response = await fetch(buildUrl(`/api/holiday-calendars/${calendarId}`), {
      method: 'DELETE',
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  getHolidayCalendarHolidays: async (calendarId, year, month) => {
    const params = new URLSearchParams({ year });
    if (month) params.append('month', month);
    const response = await fetch(buildUrl(`/api/holiday-calendars/${calendarId}/holidays?${params}`), {
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // Commits
  getCommits: async (params) => {
    const url = new URL(buildUrl('/api/v1/commits/'));
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        if (Array.isArray(params[key])) {
          params[key].forEach(value => url.searchParams.append(key, value));
        } else {
          url.searchParams.append(key, params[key]);
        }
      }
    });
    const response = await fetch(url.toString(), { 
      headers: api.getHeaders(), 
      credentials: 'include',
    });
    return handleResponse(response);
  },

  getUserCommitAnalytics: async (params) => {
    const queryParams = { ...params };
    if (queryParams.team_ids && queryParams.team_ids.length === 0) {
      delete queryParams.team_ids;
    }
    const url = new URL(buildUrl('/api/v1/commits/analytics/by-user/'));
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] !== undefined && queryParams[key] !== null) {
        // Si es un array (como team_ids), se añade múltiples veces o se une con comas según lo espere el backend
        // FastAPI con Query(None) para List[str] espera múltiples parámetros con el mismo nombre, ej: team_ids=1&team_ids=2
        if (Array.isArray(queryParams[key])) {
          queryParams[key].forEach(value => url.searchParams.append(key, value));
        } else {
          url.searchParams.append(key, queryParams[key]);
        }
      }
    });
    const response = await fetch(url.toString(), { 
      headers: api.getHeaders(), 
      credentials: 'include',
    });
    return handleResponse(response);
  },

  getTeamOptions: async () => {
    const response = await fetch(buildUrl('/api/v1/teams/options/'), {
      headers: api.getHeaders(), 
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // Analítica de score LLM por periodo
  getScoreByPeriod: async (params = {}) => {
    const url = new URL(buildUrl('/api/v1/commits/analytics/score-by-period/'));
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        if (Array.isArray(params[key])) {
          params[key].forEach(value => url.searchParams.append(key, value));
        } else {
          url.searchParams.append(key, params[key]);
        }
      }
    });
    const response = await fetch(url.toString(), { 
      headers: api.getHeaders(), 
      credentials: 'include',
    });
    return handleResponse(response);
  },

  sendFeedback: async (message) => {
    const response = await fetch(buildUrl('/api/v1/feedback'), {
      method: 'POST',
      headers: api.getHeaders(),
      body: JSON.stringify({ message }),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // JIRA Dashboard
  getJiraConfig: async () => {
    const response = await fetch(buildUrl('/api/jira/config'), {
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  updateJiraConfig: async (config) => {
    const response = await fetch(buildUrl('/api/jira/config'), {
      method: 'PUT',
      headers: api.getHeaders(),
      body: JSON.stringify(config),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  getJiraIssue: async (issueKey) => {
    const response = await fetch(buildUrl(`/api/jira/issue/${encodeURIComponent(issueKey)}`), {
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  testJiraConnection: async () => {
    const response = await fetch(buildUrl('/api/jira/test-connection'), {
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  getJiraDashboardUsers: async () => {
    const response = await fetch(buildUrl('/api/jira/dashboard/users'), {
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  getJiraDashboardTeams: async () => {
    const response = await fetch(buildUrl('/api/jira/dashboard/teams'), {
      headers: api.getHeaders(),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  getJiraProductivitySummary: async (requestData) => {
    const response = await fetch(buildUrl('/api/jira/dashboard/productivity/summary'), {
      method: 'POST',
      headers: api.getHeaders(),
      body: JSON.stringify(requestData),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  getJiraProductivityData: async (requestData) => {
    const response = await fetch(buildUrl('/api/jira/dashboard/productivity'), {
      method: 'POST',
      headers: api.getHeaders(),
      body: JSON.stringify(requestData),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  getJiraCompletedData: async (requestData) => {
    const response = await fetch(buildUrl('/api/jira/dashboard/completed'), {
      method: 'POST',
      headers: api.getHeaders(),
      body: JSON.stringify(requestData),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  getJiraStuckIssues: async (requestData) => {
    const response = await fetch(buildUrl('/api/jira/dashboard/stuck'), {
      method: 'POST',
      headers: api.getHeaders(),
      body: JSON.stringify(requestData),
      credentials: 'include',
    });
    return handleResponse(response);
  },

  getJiraEvolutionData: async (requestData) => {
    const response = await fetch(buildUrl('/api/jira/dashboard/evolution'), {
      method: 'POST',
      headers: api.getHeaders(),
      body: JSON.stringify(requestData),
      credentials: 'include',
    });
    return handleResponse(response);
  },
};

export { api }; 